import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import InterdicaoCcoCard from "./InterdicaoCcoCard";
import styles from "./InterdicoesCcoPage.module.css";
import {
  finalizarBaixaInterdicoesCco,
  listarInterdicoesCco,
  salvarDadosInterdicoesCco,
} from "./interdicoesCco.api";
import {
  RT,
  connectRealtime,
  joinRoom,
  leaveRoom,
  makeCimInterdicoesRoom,
  off as offRealtime,
  on as onRealtime,
} from "../../../api/realtime";
import {
  FILTER_ALL,
  pad2,
  toTodayYMD,
  parseYMD,
  toBRDate,
  normalizeText,
} from "../core/cimInterdicoes.utils";
import { listarSecoesBloqueioDistinct } from "../bases/secao-bloqueio/secaoBloqueio.api";

const DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];
const CCO_STATUS_OPTIONS = ["Aguardando CCO", "Aprovado CCO", "Aguardando Finalização CCO"];
const CCO_STATUS_SET = new Set(CCO_STATUS_OPTIONS);
const CCO_STATUS_ORDER = {
  "Aguardando CCO": 0,
  "Aprovado CCO": 1,
  "Aguardando Finalização CCO": 2,
};

function resolveHomePath(pathname) {
  if (!pathname || pathname === "/") return "/";

  return pathname
    .replace(/\/cim\/interdicoescco\/?$/, "/")
    .replace(/\/interdicoes\/numeros\/?$/, "/") || "/";
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1, 12, 0, 0);
}

function monthLabel(date) {
  const label = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function buildMonthGrid(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthStart = new Date(year, month, 1, 12, 0, 0);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
}

function buildSbMesaMap(rows = []) {
  return rows.reduce((acc, row) => {
    const sb = String(row?.secaodebloqueio ?? row?.secao_bloqueio ?? row?.sb ?? "").trim();
    const mesa = String(row?.mesa_cco ?? row?.mesaCco ?? "").trim();
    const ativo = Number(row?.ativo ?? 1);

    if (!sb || !mesa || ativo !== 1) return acc;

    const key = normalizeText(sb);
    if (!acc.has(key)) acc.set(key, new Set());
    acc.get(key).add(mesa);
    return acc;
  }, new Map());
}

function resolveMesaCco(row, sbMesaMap = new Map()) {
  const directMesa = String(row?.mesaCco ?? row?.mesa_cco ?? "").trim();
  if (directMesa) return directMesa;

  const sb = String(row?.sb ?? row?.secaodebloqueio ?? row?.secao_bloqueio ?? "").trim();
  if (!sb) return "";

  const mesas = sbMesaMap.get(normalizeText(sb));
  if (!mesas?.size) return "";

  return Array.from(mesas).sort((left, right) =>
    left.localeCompare(right, "pt-BR", { sensitivity: "base" })
  )[0];
}

function mapApiInterdicaoToUi(row = {}, sbMesaMap = new Map()) {
  const temPrevisao =
    row?.temPrevisao === true ||
    row?.temPrevisao === 1 ||
    row?.tem_previsao === 1 ||
    row?.tem_previsao === true;

  return {
    id: row?.id || `row-${Date.now()}`,
    codigoUnico: row?.codigoUnico || row?.codigo_unico || "",
    tipoInterdicao: row?.tipoInterdicao || row?.tipo_interdicao || "",
    identificador: row?.identificador || "",
    sb: row?.sb || row?.secaodebloqueio || row?.secao_bloqueio || "",
    sub: row?.sub != null ?String(row.sub) : "",
    kmInicial: row?.kmInicial || row?.km || row?.km_inicial || "",
    kmFinal: row?.kmFinal || row?.km_final || "",
    dataInicio: row?.dataInicio || row?.data_inicio || "",
    horaInicio: row?.horaInicio || row?.hora_inicio || "",
    dataFim: row?.dataFim || row?.data_fim || "",
    horaFim: row?.horaFim || row?.hora_fim || "",
    motivo: row?.motivo || "",
    observacao: row?.observacao || "",
    acionamento: row?.acionamento || "",
    mesaCco: resolveMesaCco(row, sbMesaMap),
    temPrevisao,
    status: row?.status || "",
    numeroInterdicao: row?.numeroInterdicao || row?.numero_interdicao || "",
    sbInterdicao: row?.sbInterdicao || row?.sb_interdicao || "",
  };
}

function SidebarDateDropdown({ value, onChange, placeholder = "Todas as datas" }) {
  const rootRef = useRef(null);
  const selectedDate = parseYMD(value === FILTER_ALL ?toTodayYMD() : value) || new Date();
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 12, 0, 0)
  );

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setViewMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 12, 0, 0));
    }
  }, [open, selectedDate]);

  const calendarDays = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
  const today = toTodayYMD();
  const currentMonth = viewMonth.getMonth();

  return (
    <div className={`${styles.dateDropdown} ${open ?styles.dateDropdownOpen : ""}`} ref={rootRef}>
      <button
        type="button"
        className={styles.dateButton}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={styles.dateButtonValue}>
          {value === FILTER_ALL ?placeholder : toBRDate(value)}
        </span>
      </button>

      {open ?(
        <div className={styles.datePanel} role="dialog" aria-label="Selecionar data">
          <div className={styles.datePanelHead}>
            <button
              type="button"
              className={styles.dateNav}
              onClick={() => setViewMonth((current) => addMonths(current, -1))}
              aria-label="Mês anterior"
            >
              {"<"}
            </button>
            <div className={styles.datePanelTitle}>{monthLabel(viewMonth)}</div>
            <button
              type="button"
              className={styles.dateNav}
              onClick={() => setViewMonth((current) => addMonths(current, 1))}
              aria-label="Próximo mês"
            >
              {">"}
            </button>
          </div>

          <div className={styles.dateWeekdays}>
            {DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className={styles.dateWeekday}>
                {weekday}
              </span>
            ))}
          </div>

          <div className={styles.dateGrid}>
            {calendarDays.map((day) => {
              const dayKey = `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}`;
              const isOutside = day.getMonth() !== currentMonth;
              const isSelected = dayKey === value;
              const isToday = dayKey === today;

              return (
                <button
                  type="button"
                  key={dayKey}
                  className={[
                    styles.dateDay,
                    isOutside ?styles.dateDayOutside : "",
                    isSelected ?styles.dateDaySelected : "",
                    isToday ?styles.dateDayToday : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => {
                    onChange(dayKey);
                    setOpen(false);
                  }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className={styles.datePanelFoot}>
            <button type="button" className={styles.dateAction} onClick={() => onChange(today)}>
              Hoje
            </button>
            <button
              type="button"
              className={`${styles.dateAction} ${styles.dateActionGhost}`}
              onClick={() => onChange(FILTER_ALL)}
            >
              Limpar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SidebarMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Todos",
  title = "Selecione",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const selected = Array.isArray(value) ?value : value ?[value] : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const buttonLabel = selected.length
    ?`${selected.length} selecionado${selected.length > 1 ?"s" : ""}`
    : placeholder;

  const handleToggle = (optionValue) => {
    const next = new Set(selectedSet);
    if (next.has(optionValue)) next.delete(optionValue);
    else next.add(optionValue);
    onChange(Array.from(next));
  };

  return (
    <div className={styles.msRoot} ref={rootRef}>
      <button
        className={`${styles.msBtn} ${open ?styles.open : ""}`}
        type="button"
        onClick={() => setOpen((current) => !current)}
      >
        <span className={`${styles.msText} ${selected.length ?"" : styles.isPlaceholder}`}>{buttonLabel}</span>
        <span className={styles.caret}>▾</span>
      </button>

      {open ?(
        <div className={styles.msPanel}>
          <div className={styles.msHeader}>
            <div className={styles.msHeaderCopy}>
              <span className={styles.msHeaderTitle}>{title}</span>
              <span className={styles.msHeaderMeta}>{selected.length || 0} selecionado(s)</span>
            </div>
            <button type="button" className={styles.msActionBtn} onClick={() => onChange([])}>
              Limpar
            </button>
          </div>

          <div className={styles.msListWrap}>
            <div className={styles.msList}>
              {options.map((option) => {
                const checked = selectedSet.has(option.value);
                return (
                  <button
                    type="button"
                    key={option.value}
                    className={`${styles.msItem} ${checked ?styles.msItemSelected : ""}`}
                    onClick={() => handleToggle(option.value)}
                  >
                    <span className={styles.msLabel}>{option.label}</span>
                  </button>
                );
              })}

              {!options.length ?<div className={styles.msEmpty}>Nenhum resultado encontrado.</div> : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SidebarSingleSelect({
  options = [],
  value = FILTER_ALL,
  onChange,
  placeholder = "Todos",
  title = "Selecione",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const selectedOption = options.find((option) => option.value === value);
  const buttonLabel = selectedOption?.label || placeholder;

  return (
    <div className={styles.msRoot} ref={rootRef}>
      <button
        className={`${styles.msBtn} ${open ?styles.open : ""}`}
        type="button"
        onClick={() => setOpen((current) => !current)}
      >
        <span className={`${styles.msText} ${selectedOption ?"" : styles.isPlaceholder}`}>{buttonLabel}</span>
        <span className={styles.caret}>▾</span>
      </button>

      {open ?(
        <div className={styles.msPanel}>
          <div className={styles.msHeader}>
            <div className={styles.msHeaderCopy}>
              <span className={styles.msHeaderTitle}>{title}</span>
            </div>
            <button
              type="button"
              className={styles.msActionBtn}
              onClick={() => {
                onChange(FILTER_ALL);
                setOpen(false);
              }}
            >
              Limpar
            </button>
          </div>

          <div className={styles.msListWrap}>
            <div className={styles.msList}>
              {options.map((option) => {
                const selected = option.value === value;
                return (
                  <button
                    type="button"
                    key={option.value}
                    className={`${styles.msItem} ${selected ?styles.msItemSelected : ""}`}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <span className={styles.msLabel}>{option.label}</span>
                  </button>
                );
              })}

              {!options.length ?<div className={styles.msEmpty}>Nenhum resultado encontrado.</div> : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function InterdicoesCcoPage({ onBack }) {
  const navigate = useNavigate();
  const [interdicoes, setInterdicoes] = useState([]);
  const [busca, setBusca] = useState("");
  const [filtroData, setFiltroData] = useState(FILTER_ALL);
  const [filtroMesaCco, setFiltroMesaCco] = useState(FILTER_ALL);
  const [filtroStatus, setFiltroStatus] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [saveError, setSaveError] = useState("");
  const sidebarRef = useRef(null);
  const summaryRef = useRef(null);
  const realtimeReloadTimerRef = useRef(null);
  const sbMesaMapRef = useRef(new Map());

  const handleBackHome = () => {
    const homePath =
      typeof window !== "undefined" ?resolveHomePath(window.location.pathname) : "/";

    if (typeof window !== "undefined") {
      window.location.replace(homePath);
      return;
    }

    if (typeof onBack === "function") {
      onBack();
      return;
    }

    navigate(homePath, { replace: true });
  };

  const carregarInterdicoes = useCallback(async () => {
    try {
      setLoadError("");
      const [result, secoesResult] = await Promise.all([
        listarInterdicoesCco({ limit: 5000, offset: 0 }),
        listarSecoesBloqueioDistinct({ ativo: 1, limit: 5000, offset: 0 }).catch(() => ({
          rows: [],
        })),
      ]);
      const sbMesaMap = buildSbMesaMap(Array.isArray(secoesResult?.rows) ?secoesResult.rows : []);
      sbMesaMapRef.current = sbMesaMap;
      const rows = Array.isArray(result.rows)
        ?result.rows.map((row) => mapApiInterdicaoToUi(row, sbMesaMap))
        : [];
      setInterdicoes(rows.filter((item) => CCO_STATUS_SET.has(item.status)));
    } catch (err) {
      setLoadError(err?.message || "Não foi possível carregar as interdições do banco.");
    }
  }, []);

  useEffect(() => {
    carregarInterdicoes();
  }, [carregarInterdicoes]);

  useEffect(() => {
    const room = makeCimInterdicoesRoom();

    const handleRealtimeChanged = () => {
      if (realtimeReloadTimerRef.current) {
        window.clearTimeout(realtimeReloadTimerRef.current);
      }

      realtimeReloadTimerRef.current = window.setTimeout(() => {
        carregarInterdicoes();
      }, 120);
    };

    connectRealtime().catch(() => {});
    if (room) joinRoom(room);
    onRealtime(RT.CIM_INTERDICOES_CHANGED, handleRealtimeChanged);

    return () => {
      offRealtime(RT.CIM_INTERDICOES_CHANGED, handleRealtimeChanged);
      if (room) leaveRoom(room);
      if (realtimeReloadTimerRef.current) {
        window.clearTimeout(realtimeReloadTimerRef.current);
        realtimeReloadTimerRef.current = null;
      }
    };
  }, [carregarInterdicoes]);

  const statusOptions = useMemo(
    () => CCO_STATUS_OPTIONS.map((option) => ({ value: option, label: option })),
    []
  );

  const mesaCcoOptions = useMemo(() => {
    const values = Array.from(
      new Set(interdicoes.map((item) => String(item?.mesaCco || "").trim()).filter(Boolean))
    ).sort((left, right) => left.localeCompare(right, "pt-BR", { sensitivity: "base" }));

    return [
      { value: FILTER_ALL, label: "Todas as mesas" },
      ...values.map((value) => ({ value, label: value })),
    ];
  }, [interdicoes]);

  const visiveis = useMemo(() => {
    const query = normalizeText(busca);

    return interdicoes
      .filter((item) => {
        const matchesAllowedStatus = CCO_STATUS_SET.has(item.status);
        const matchesQuery =
          !query ||
          normalizeText(
            [
              item.codigoUnico,
              item.identificador,
              item.sb,
              item.sub,
            item.motivo,
            item.acionamento,
            item.numeroInterdicao,
            item.sbInterdicao,
            item.mesaCco,
            item.tipoInterdicao,
          ].join(" ")
        ).includes(query);

        const matchesData = filtroData === FILTER_ALL || item.dataInicio === filtroData;
        const matchesMesaCco =
          filtroMesaCco === FILTER_ALL ||
          normalizeText(item.mesaCco) === normalizeText(filtroMesaCco);
        const matchesStatus = !filtroStatus.length || filtroStatus.includes(item.status);

        return (
          matchesAllowedStatus &&
          matchesQuery &&
          matchesData &&
          matchesMesaCco &&
          matchesStatus
        );
      })
      .sort((left, right) => {
        const numberDiff =
          Number(Boolean(left.numeroInterdicao)) - Number(Boolean(right.numeroInterdicao));
        if (numberDiff !== 0) return numberDiff;

        const statusDiff =
          (CCO_STATUS_ORDER[left.status] ?? 99) - (CCO_STATUS_ORDER[right.status] ?? 99);
        if (statusDiff !== 0) return statusDiff;

        const rightId = Number.parseInt(String(right.id ?? ""), 10);
        const leftId = Number.parseInt(String(left.id ?? ""), 10);
        if (Number.isFinite(rightId) && Number.isFinite(leftId) && rightId !== leftId) {
          return rightId - leftId;
        }

        return String(right.codigoUnico || right.identificador || right.id).localeCompare(
          String(left.codigoUnico || left.identificador || left.id)
        );
      });
  }, [busca, filtroData, filtroMesaCco, filtroStatus, interdicoes]);

  const resumo = useMemo(() => {
    const total = visiveis.length;
    const aguardandoCco = visiveis.filter((item) => item.status === "Aguardando CCO").length;
    const aprovadoCco = visiveis.filter((item) => item.status === "Aprovado CCO").length;
    const aguardandoFinalizacaoCco = visiveis.filter(
      (item) => item.status === "Aguardando Finalização CCO"
    ).length;

    return { total, aguardandoCco, aprovadoCco, aguardandoFinalizacaoCco };
  }, [visiveis]);

  useLayoutEffect(() => {
    const apply = () => {
      const el = sidebarRef.current;
      if (!el) return;

      const h = el.clientHeight || window.innerHeight;
      const BASE = 1080;
      const s = h / BASE;
      const LIFT = 26 * s;

      const map = {
        "--y-menu": 46 * s,
        "--y-title-data": (216 - 26) * s - LIFT,
        "--y-data": 216 * s - LIFT,
        "--y-title-busca": (304 - 26) * s - LIFT,
        "--y-busca": 304 * s - LIFT,
        "--y-title-mesa-cco": (394 - 26) * s - LIFT,
        "--y-mesa-cco": 394 * s - LIFT,
        "--y-title-status": (482 - 26) * s - LIFT,
        "--y-status": 482 * s - LIFT,
      };

      const CTRL_H = 42;
      const BOTTOM = 22;
      const SUMMARY_GAP = 18;
      const yLimpar = h - BOTTOM - CTRL_H - LIFT;
      const summaryHeight = Math.max(108, Math.ceil(summaryRef.current?.offsetHeight || 0));
      const yResumo = yLimpar - SUMMARY_GAP - summaryHeight;

      Object.entries(map).forEach(([key, value]) => {
        el.style.setProperty(key, `${Math.round(value)}px`);
      });
      el.style.setProperty("--y-resumo", `${Math.round(yResumo)}px`);
      el.style.setProperty("--y-limpar", `${Math.round(yLimpar)}px`);
    };

    apply();
    window.addEventListener("resize", apply);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ?new ResizeObserver(() => apply())
        : null;

    if (resizeObserver) {
      if (sidebarRef.current) resizeObserver.observe(sidebarRef.current);
      if (summaryRef.current) resizeObserver.observe(summaryRef.current);
    }

    return () => {
      window.removeEventListener("resize", apply);
      resizeObserver?.disconnect();
    };
  }, [resumo]);

  const handleSaveDadosCco = async (id, nextValues) => {
    try {
      setSaveError("");
      setSavingId(id);

      const updated = await salvarDadosInterdicoesCco(id, nextValues);
      const updatedItem = mapApiInterdicaoToUi(updated, sbMesaMapRef.current);

      setInterdicoes((current) => {
        const next = current.map((item) => (item.id === id ?updatedItem : item));
        return next.filter((item) => CCO_STATUS_SET.has(item.status));
      });
    } catch (err) {
      setSaveError(err?.message || "Não foi possível salvar os dados do CCO.");
    } finally {
      setSavingId(null);
    }
  };

  const handleFinalizarBaixa = async (id) => {
    try {
      setSaveError("");
      setSavingId(id);

      const updated = await finalizarBaixaInterdicoesCco(id);
      const updatedItem = mapApiInterdicaoToUi(updated, sbMesaMapRef.current);

      setInterdicoes((current) => {
        const next = current.map((item) => (item.id === id ?updatedItem : item));
        return next.filter((item) => CCO_STATUS_SET.has(item.status));
      });
    } catch (err) {
      setSaveError(err?.message || "Não foi possível dar baixa final no CCO.");
    } finally {
      setSavingId(null);
    }
  };

  const limparFiltros = () => {
    setBusca("");
    setFiltroData(FILTER_ALL);
    setFiltroMesaCco(FILTER_ALL);
    setFiltroStatus([]);
  };

  return (
    <div className={styles.root}>
      <aside className={styles.sidebar} ref={sidebarRef}>
        <button type="button" className={`${styles.btn} ${styles.abs} ${styles.posMenu}`} onClick={handleBackHome}>
          Menu Principal
        </button>

        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleData}`}>Filtrar Data:</div>
        <div className={`${styles.abs} ${styles.posData}`}>
          <SidebarDateDropdown value={filtroData} onChange={setFiltroData} />
        </div>

        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleBusca}`}>Buscar:</div>
        <div className={`${styles.abs} ${styles.posBusca}`}>
          <input
            type="text"
            className={styles.inputLike}
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            placeholder="Código, SB, motivo ou número"
          />
        </div>

        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleMesaCco}`}>Filtrar Mesa CCO:</div>
        <div className={`${styles.abs} ${styles.posMesaCco}`}>
          <SidebarSingleSelect
            options={mesaCcoOptions}
            value={filtroMesaCco}
            onChange={setFiltroMesaCco}
            placeholder="Todas as mesas"
            title="Filtrar Mesa CCO"
          />
        </div>

        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleStatus}`}>Filtrar Status:</div>
        <div className={`${styles.abs} ${styles.posStatus}`}>
          <SidebarMultiSelect
            options={statusOptions}
            value={filtroStatus}
            onChange={setFiltroStatus}
            placeholder="Todos os status"
            title="Filtrar Status"
          />
        </div>

        <div className={`${styles.abs} ${styles.posResumo}`}>
          <div className={styles.sideSummary} ref={summaryRef}>
            <div className={styles.sideSummaryHead}>
              <div className={styles.sideSummaryTitle}>Resumo do período</div>
              <div className={styles.sideSummaryRange}>
                {filtroData === FILTER_ALL ?"Todas as datas" : toBRDate(filtroData)}
              </div>
            </div>

            <div className={styles.sideSummaryGrid}>
              <div className={styles.mini}>
                <div className={styles.miniLbl}>Total</div>
                <div className={styles.miniVal}>{resumo.total}</div>
              </div>
              <div className={styles.mini}>
                <div className={styles.miniLbl}>Aguardando CCO</div>
                <div className={styles.miniVal}>{resumo.aguardandoCco}</div>
              </div>
              <div className={`${styles.mini} ${styles.miniWide}`}>
                <div className={styles.miniLbl}>Aprovado CCO</div>
                <div className={styles.miniVal}>{resumo.aprovadoCco}</div>
              </div>
              <div className={`${styles.mini} ${styles.miniWide}`}>
                <div className={styles.miniLbl}>Ag. Finalização CCO</div>
                <div className={styles.miniVal}>{resumo.aguardandoFinalizacaoCco}</div>
              </div>
            </div>
          </div>
        </div>

        <div className={`${styles.abs} ${styles.posLimpar}`}>
          <button
            type="button"
            className={styles.btn}
            onClick={limparFiltros}
            title="Limpar filtros"
          >
            Limpar filtros
          </button>
        </div>
      </aside>

      <div className={styles.content}>
        <div className={styles.contentHeader}>
          <h2 className={styles.title}>Cards de Interdições CCO</h2>
          <p className={styles.subtitle}>
            Use os cartões para preencher o número e a SB das interdições aguardando ação do CCO.
          </p>
        </div>

        <div className={styles.contentBody}>
          {loadError ?<div className={styles.emptyState}>{loadError}</div> : null}
          {saveError ?<div className={styles.emptyState}>{saveError}</div> : null}

          {visiveis.length ?(
            <div className={styles.grid}>
              {visiveis.map((item) => (
                <InterdicaoCcoCard
                  key={item.id}
                  interdicao={item}
                  onSave={handleSaveDadosCco}
                  onFinalize={handleFinalizarBaixa}
                  saving={savingId === item.id}
                />
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>Nenhuma interdição encontrada para os filtros atuais.</div>
          )}
        </div>
      </div>
    </div>
  );
}
